import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { buildApiUrl, buildPublicFileUrl } from "../config/api";
import "./HeroSlider.css";
import "./HeroSliderMobile.css";

const HOMEPAGE_HERO_IMAGE =
  "/uploads/public/hero/desktop/hero-sunset-desktop-1920x720.jpg";

const heroSrcSet = (folder, name, widths, extension) =>
  widths
    .map(
      (width) =>
        `/uploads/public/hero/${folder}/hero-sunset-${name}-${width}.${extension} ${width.split("x")[0]}w`
    )
    .join(", ");

const HOMEPAGE_HERO_SOURCES = [
  {
    media: "(max-width: 720px)",
    type: "image/avif",
    srcSet: heroSrcSet("mobile", "mobile", ["720x480", "1080x720", "1440x960"], "avif"),
  },
  {
    media: "(max-width: 720px)",
    type: "image/webp",
    srcSet: heroSrcSet("mobile", "mobile", ["720x480", "1080x720", "1440x960"], "webp"),
  },
  {
    media: "(max-width: 720px)",
    type: "image/jpeg",
    srcSet: heroSrcSet("mobile", "mobile", ["720x480", "1080x720", "1440x960"], "jpg"),
  },
  {
    media: "(min-width: 721px) and (max-width: 1080px)",
    type: "image/avif",
    srcSet: heroSrcSet("tablet", "tablet", ["1200x800", "1536x1024", "1920x1280"], "avif"),
  },
  {
    media: "(min-width: 721px) and (max-width: 1080px)",
    type: "image/webp",
    srcSet: heroSrcSet("tablet", "tablet", ["1200x800", "1536x1024", "1920x1280"], "webp"),
  },
  {
    media: "(min-width: 721px) and (max-width: 1080px)",
    type: "image/jpeg",
    srcSet: heroSrcSet("tablet", "tablet", ["1200x800", "1536x1024", "1920x1280"], "jpg"),
  },
  {
    media: "(min-width: 1081px)",
    type: "image/avif",
    srcSet: heroSrcSet("desktop", "desktop", ["1600x600", "1920x720", "2560x960", "3840x1440"], "avif"),
  },
  {
    media: "(min-width: 1081px)",
    type: "image/webp",
    srcSet: heroSrcSet("desktop", "desktop", ["1600x600", "1920x720", "2560x960", "3840x1440"], "webp"),
  },
  {
    media: "(min-width: 1081px)",
    type: "image/jpeg",
    srcSet: heroSrcSet("desktop", "desktop", ["1600x600", "1920x720", "2560x960", "3840x1440"], "jpg"),
  },
];

const defaultCopy = {
  eyebrow_text: "Original Photography Marketplace",
  title_text: "Visual Stories. Timeless Moments.",
  subtitle_text:
    "Original photography downloads and image licenses for homes, creators, publishers, and brands.",
  primary_button_text: "Shop Photography",
  primary_button_link: "/shop?brand=mayachitro",
  secondary_button_text: "Explore Collections",
  secondary_button_link: "/collections",
};

const fallbackSlide = {
  id: "mayachitro-signature",
  layout_type: "photography_full_bleed",
  layout_variant: "signature",
  theme_style: "dark",
  hero_size: "large",
  main_image_path: HOMEPAGE_HERO_IMAGE,
  main_image_fit: "cover",
  main_image_position: "center center",
  eyebrow_text: "The MayaChitro Archive · Bangladesh",
  title_text: "Photography that stays with you.",
  subtitle_text:
    "Collect original, story-led photographs for remarkable spaces, publications, and brands.",
  primary_button_text: "Explore the collection",
  primary_button_link: "/shop?brand=mayachitro",
  secondary_button_text: "License an image →",
  secondary_button_link: "/licensing",
  tag_1: "Artist Original",
  tag_2: "Museum Quality",
  tag_3: "Instant Delivery",
  trust_bar_enabled: 1,
};

const toAssetUrl = (path) => {
  if (!path || typeof path !== "string") return "";

  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) return path;
  if (path.startsWith("/uploads") || path.startsWith("uploads/")) {
    return buildPublicFileUrl(path);
  }

  return path;
};

const toAssetSrcSet = (srcSet) =>
  String(srcSet || "")
    .split(",")
    .map((candidate) => {
      const [path, descriptor] = candidate.trim().split(/\s+/, 2);
      return [toAssetUrl(path), descriptor].filter(Boolean).join(" ");
    })
    .filter(Boolean)
    .join(", ");

const safeClass = (value) => {
  return String(value || "default")
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "-");
};

const valueOrDefault = (value, fallback) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return fallback;
  }

  return value;
};

const SmartButton = ({ to, children, variant = "primary" }) => {
  if (!to || !children) return null;

  const className = `hero-btn hero-btn-${variant}`;

  if (/^https?:\/\//i.test(to)) {
    return (
      <a href={to} className={className} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
};

const ImageFallback = ({ label = "MayaChitro" }) => {
  return (
    <div className="hero-image-fallback">
      <span>{label}</span>
    </div>
  );
};

const HeroImage = ({ src, mobileSrc, sources, alt, className = "", style }) => {
  const [failed, setFailed] = useState(false);
  const imageSrc = toAssetUrl(src);

  if (!imageSrc || failed) {
    return <ImageFallback />;
  }

  const image = (
    <img
      className={className}
      src={imageSrc}
      sizes="100vw"
      alt={alt || "MayaChitro visual"}
      loading="eager"
      decoding="async"
      fetchPriority="high"
      style={style}
      onError={() => setFailed(true)}
    />
  );

  if (!mobileSrc && !sources?.length) return image;

  return (
    <picture className="hero-responsive-picture">
      {sources?.map((source) => (
        <source
          key={`${source.media || "all"}-${source.type || "image"}`}
          media={source.media}
          type={source.type}
          srcSet={toAssetSrcSet(source.srcSet)}
          sizes={source.sizes || "100vw"}
        />
      ))}
      {mobileSrc ? (
        <source media="(max-width: 1080px)" srcSet={toAssetUrl(mobileSrc)} />
      ) : null}
      {image}
    </picture>
  );
};

const HeroActions = ({ slide, light = false }) => {
  const primaryText = valueOrDefault(
    slide.primary_button_text,
    defaultCopy.primary_button_text
  );

  const primaryLink = valueOrDefault(
    slide.primary_button_link,
    defaultCopy.primary_button_link
  );

  const secondaryText = valueOrDefault(
    slide.secondary_button_text,
    defaultCopy.secondary_button_text
  );

  const secondaryLink = valueOrDefault(
    slide.secondary_button_link,
    defaultCopy.secondary_button_link
  );

  return (
    <div className="hero-actions">
      <SmartButton to={primaryLink} variant={light ? "dark" : "primary"}>
        {primaryText}
      </SmartButton>

      <SmartButton
        to={secondaryLink}
        variant={light ? "light-outline" : "outline"}
      >
        {secondaryText}
      </SmartButton>
    </div>
  );
};

const getMainImageFit = (slide) => {
  if (slide.main_image_fit === "cover" || slide.main_image_fit === "contain") {
    return slide.main_image_fit;
  }

  return "contain";
};

const getImageStyle = (slide) => {
  return {
    objectPosition: slide.main_image_position || "center center",
    "--hero-image-scale": Number(slide.main_image_scale || 1),
  };
};

const getSlideText = (slide, type = "default") => {
  if (type === "photography") {
    return {
      eyebrow: "Original Photography · Instant Download",
      title: "Photography with story, light, and emotion.",
      subtitle: "Original photographs of people, nature, places, and quiet moments—ready for collectors and creative projects worldwide.",
    };
  }

  if (type === "cinematic") {
    return {
      eyebrow: "Original Photography · Instant Download",
      title: "Photography with story, light, and emotion.",
      mobileTitle: "Stories in light.",
      subtitle: "Original photographs of people, nature, places, and quiet moments—ready for collectors and creative projects worldwide.",
      mobileSubtitle: "Original photographs, ready to collect.",
    };
  }

  if (type === "wallart") {
    return {
      eyebrow: valueOrDefault(slide.eyebrow_text, "Cloudy Pixel Studio"),
      title: valueOrDefault(
        slide.title_text,
        "Printable Wall Art for Modern Interiors"
      ),
      subtitle: valueOrDefault(
        slide.subtitle_text,
        "Downloadable posters and art prints for calm, vintage, cozy, and creative spaces."
      ),
    };
  }

  return {
    eyebrow: valueOrDefault(slide.eyebrow_text, defaultCopy.eyebrow_text),
    title: valueOrDefault(slide.title_text, defaultCopy.title_text),
    subtitle: valueOrDefault(slide.subtitle_text, defaultCopy.subtitle_text),
  };
};

const getSideCards = (slide) => {
  const cards = [
    {
      image: slide.side_image_1,
      title: slide.side_image_1_title,
      label: "Wall Art",
    },
    {
      image: slide.side_image_2,
      title: slide.side_image_2_title,
      label: "Photo",
    },
    {
      image: slide.side_image_3,
      title: slide.side_image_3_title,
      label: "License",
    },
  ].filter((item) => item.image || item.title);

  const count = Number(slide.side_cards_count ?? 2);

  return cards.slice(0, Math.max(0, Math.min(count, 3)));
};

const SideCard = ({ image, title, label }) => {
  if (!image && !title) return null;

  return (
    <div className="hero-side-card">
      {image ? (
        <HeroImage src={image} alt={title || "MayaChitro featured visual"} />
      ) : (
        <ImageFallback label={label || "Visual"} />
      )}

      <div className="hero-side-overlay">
        {label ? <span>{label}</span> : null}
        <strong>{title || "Curated Visual"}</strong>
      </div>
    </div>
  );
};

const HeroTitle = ({ title, mobileTitle }) => {
  const parts = String(title || "").split(/\s+(?=light\b)/i);

  return (
    <>
      <h1 className={mobileTitle ? "hero-title-desktop" : ""}>
        {parts.map((part, index) => (
          <React.Fragment key={`${part}-${index}`}>
            {index > 0 && <br />}
            {part}
          </React.Fragment>
        ))}
      </h1>
      {mobileTitle ? <h1 className="hero-title-mobile">{mobileTitle}</h1> : null}
    </>
  );
};

const SplitPremiumSlide = ({ slide }) => {
  const text = getSlideText(slide);
  const sideCards = getSideCards(slide);
  const imageFit = getMainImageFit(slide);

  return (
    <div className="hero-stage hero-stage-split">
      <div className="hero-copy">
        <p className="hero-eyebrow">{text.eyebrow}</p>
        <HeroTitle title={text.title} />
        <p className="hero-subtitle">{text.subtitle}</p>
        <HeroActions slide={slide} />
      </div>

      <div className={`hero-feature-card hero-main-fit-${imageFit}`}>
        <HeroImage
          src={slide.main_image_path}
          alt={text.title}
          style={getImageStyle(slide)}
        />
      </div>

      {sideCards.length ? (
        <div className={`hero-side-stack side-count-${sideCards.length}`}>
          {sideCards.map((card, index) => (
            <SideCard
              key={`${card.title || "side"}-${index}`}
              image={card.image}
              title={card.title}
              label={card.label}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const CinematicBannerSlide = ({ slide }) => {
  const text = getSlideText(slide, "cinematic");

  return (
    <div className="hero-stage hero-stage-cinematic">
      <HeroImage
        src={HOMEPAGE_HERO_IMAGE}
        sources={HOMEPAGE_HERO_SOURCES}
        alt={text.title}
        style={getImageStyle(slide)}
      />

      <div className="hero-cinematic-overlay">
        <p className="hero-eyebrow">{text.eyebrow}</p>
        <HeroTitle title={text.title} mobileTitle={text.mobileTitle} />
        <p className="hero-subtitle hero-subtitle-desktop">{text.subtitle}</p>
        <p className="hero-subtitle hero-subtitle-mobile">{text.mobileSubtitle}</p>
        <HeroActions slide={slide} />
      </div>
    </div>
  );
};

const WallArtLightSlide = ({ slide }) => {
  const text = getSlideText(slide, "wallart");
  const sideCards = getSideCards(slide);
  const imageFit = getMainImageFit(slide);

  return (
    <div className="hero-stage hero-stage-wallart">
      <div className="hero-wallart-copy">
        <p className="hero-eyebrow dark">{text.eyebrow}</p>
        <HeroTitle title={text.title} />
        <p>{text.subtitle}</p>
        <HeroActions slide={slide} light />
      </div>

      <div className="hero-wallart-gallery">
        <div className={`hero-wallart-main hero-main-fit-${imageFit}`}>
          <HeroImage
            src={slide.main_image_path}
            alt={text.title}
            style={getImageStyle(slide)}
          />
        </div>

        {sideCards.length ? (
          <div className="hero-wallart-side">
            {sideCards.slice(0, 2).map((card, index) => (
              <SideCard
                key={`${card.title || "wall"}-${index}`}
                image={card.image}
                title={card.title}
                label="Printable"
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const PhotographyValueChips = ({ slide }) => {
  const chips = [
    {
      icon: "▣",
      title: valueOrDefault(slide.tag_1, "Original Photographs"),
      text: "Captured with passion",
    },
    {
      icon: "◈",
      title: valueOrDefault(slide.tag_2, "Trusted Quality"),
      text: "High-resolution files",
    },
    {
      icon: "↓",
      title: valueOrDefault(slide.tag_3, "Instant Access"),
      text: "Download & use",
    },
  ];

  return (
    <div className="hero-photo-value-row" aria-label="MayaChitro marketplace benefits">
      {chips.map((chip) => (
        <div className="hero-photo-value" key={chip.title}>
          <span>{chip.icon}</span>
          <div>
            <strong>{chip.title}</strong>
            <small>{chip.text}</small>
          </div>
        </div>
      ))}
    </div>
  );
};

const PhotographyFullBleedSlide = ({ slide }) => {
  const text = getSlideText(slide, "photography");

  return (
    <div className="hero-stage-photography-full">
      <HeroImage
        className="hero-photo-bg"
        src={HOMEPAGE_HERO_IMAGE}
        sources={HOMEPAGE_HERO_SOURCES}
        alt={text.title}
        style={getImageStyle(slide)}
      />

      <div className="hero-photo-content-wrap">
        <div className="hero-photo-copy">
          <p className="hero-eyebrow">{text.eyebrow}</p>
          <HeroTitle title={text.title} />
          <div className="hero-photo-rule" />
          <p className="hero-subtitle">{text.subtitle}</p>
          <PhotographyValueChips slide={slide} />
          <HeroActions slide={slide} />
        </div>
      </div>
    </div>
  );
};

const TrustBar = () => {
  return (
    <div className="hero-trust-bar">
      <div>
        <span>□</span>
        <strong>Secure Checkout</strong>
        <small>Stripe-ready payment</small>
      </div>

      <div>
        <span>↓</span>
        <strong>Instant Download</strong>
        <small>Files after purchase</small>
      </div>

      <div>
        <span>✺</span>
        <strong>
          <span className="trust-label-desktop">Flexible License</span>
          <span className="trust-label-mobile">Premium Quality</span>
        </strong>
        <small>Personal & commercial</small>
      </div>

      <div>
        <span>◎</span>
        <strong>Original Visuals</strong>
        <small>Photography & wall art</small>
      </div>
    </div>
  );
};

export default function HeroSlider() {
  const [slides, setSlides] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadSlides = async () => {
      try {
        const response = await fetch(buildApiUrl("/home/hero-slides"), {
          credentials: "include",
        });

        const data = await response.json();

        if (mounted && data.success && Array.isArray(data.slides)) {
          setSlides(
            data.slides.filter((slide) => {
              const searchable = JSON.stringify(slide).toLowerCase();
              return !searchable.includes("cloudy pixel") && !searchable.includes("cloudy-pixel");
            })
          );
        }
      } catch (error) {
        console.error("Failed to load hero slides:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadSlides();

    return () => {
      mounted = false;
    };
  }, []);

  const activeSlide = useMemo(() => {
    if (!slides.length) return fallbackSlide;
    return slides[activeIndex] || slides[0];
  }, [slides, activeIndex]);

  useEffect(() => {
    if (!slides.length || paused) return undefined;

    const seconds = Number(activeSlide?.auto_slide_seconds || 7);

    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, seconds * 1000);

    return () => clearInterval(timer);
  }, [slides.length, paused, activeSlide]);

  if (loading) {
    return (
      <section className="hero-slider hero-loading">
        <div className="hero-loading-box" />
      </section>
    );
  }

  const layoutType = activeSlide.layout_type || "split_premium";
  const layoutVariant = safeClass(activeSlide.layout_variant || "default");
  const trustBarEnabled = Number(activeSlide.trust_bar_enabled ?? 1) === 1;

  return (
    <section
      className={`hero-slider hero-layout-${layoutType} hero-variant-${layoutVariant} hero-theme-${
        activeSlide.theme_style || "dark"
      } hero-size-${activeSlide.hero_size || "standard"}`}
      aria-label="MayaChitro featured marketplace visuals"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div key={`${activeSlide.id || activeIndex}-${activeIndex}`} className="hero-slide-motion">
        {layoutType === "photography_full_bleed" ? (
          <PhotographyFullBleedSlide slide={activeSlide} />
        ) : layoutType === "cinematic_banner" ? (
          <CinematicBannerSlide slide={activeSlide} />
        ) : layoutType === "wall_art_light" ? (
          <WallArtLightSlide slide={activeSlide} />
        ) : (
          <SplitPremiumSlide slide={activeSlide} />
        )}
      </div>

      {trustBarEnabled ? <TrustBar /> : null}
    </section>
  );
}
